// pages/UserAdmin/VariableSourcePicker.js
//
// Uma linha de "de onde vem o valor desta variável". Usada em DOIS lugares:
// no formulário de criação (junto do exemplo de cada {{n}}) e no diálogo de
// mapeamento da aba "Ver e editar templates".
//
// Compartilhado de propósito. Quando eram duas cópias, a lista de origens e
// os rótulos já começavam a divergir — e divergência aqui não é cosmética:
// uma tela oferecendo uma origem que a outra não conhece produz mapeamento
// que a pessoa não consegue mais editar de onde criou.

import { Box, Chip, MenuItem, Stack, TextField, Typography } from '@mui/material';

// Espelha models.TEMPLATE_VAR_SOURCES. `needs` diz qual campo extra a
// origem pede — é o que evita um `if` encadeado no JSX.
export const SOURCES = [
  { value: 'CONTACT_FIRST_NAME', label: 'Primeiro nome do contato', hint: '"Marcos Oliveira" → Marcos' },
  { value: 'CONTACT_NAME', label: 'Nome completo do contato', hint: 'Como está cadastrado' },
  { value: 'CONTACT_PHONE', label: 'Telefone do contato', hint: 'Só dígitos, com DDI' },
  { value: 'CUSTOM_FIELD', label: 'Campo personalizado', needs: 'field', hint: 'O que a sua empresa guarda em cada contato' },
  { value: 'FIXED', label: 'Texto fixo', needs: 'text', hint: 'Igual para todo mundo' },
  { value: 'ASK', label: 'Perguntar no envio', hint: 'Digitado a cada disparo' },
];

export const COMPONENT_LABELS = {
  HEADER: 'cabeçalho',
  BODY: 'mensagem',
  BUTTON_URL: 'link do botão',
};

export const slotOf = (entry) => `${entry.component}:${entry.position}`;

/** Origem incompleta: escolheu "campo personalizado" sem dizer qual, ou
 *  "texto fixo" sem o texto. Exportada porque as duas telas travam o botão
 *  de salvar com o mesmo critério. */
export function incompleteEntries(mapping) {
  return (mapping || []).filter((e) => (
    (e.source_kind === 'CUSTOM_FIELD' && !e.source_key)
    || (e.source_kind === 'FIXED' && !(e.fixed_value || '').trim())
  ));
}

/** Entrada nova com a origem padrão. `{{1}}` do corpo nasce como primeiro
 *  nome porque é o que quase todo template faz; o resto nasce perguntando,
 *  que obriga alguém a olhar antes de disparar. Espelha
 *  services/template_vars.default_mapping no backend. */
export function defaultEntry(component, position) {
  return {
    component,
    position,
    source_kind: (component === 'BODY' && position === 1)
      ? 'CONTACT_FIRST_NAME'
      : 'ASK',
    source_key: null,
    fixed_value: null,
  };
}

export default function VariableSourcePicker({
  entry, customFields = [], disabled = false, onChange, compact = false,
}) {
  const cfg = SOURCES.find((s) => s.value === entry.source_kind);
  const semCampos = customFields.length === 0;

  // Uma origem usa só um dos campos extras. Limpar o outro na troca evita
  // mandar `fixed_value` num CUSTOM_FIELD — o backend recusaria, mas o erro
  // sairia sem a pessoa entender de onde veio.
  const changeSource = (source_kind) =>
    onChange({ ...entry, source_kind, source_key: null, fixed_value: null });

  return (
    <Box>
      {!compact && (
        <Stack direction="row" alignItems="center" spacing={1} mb={1.25}>
          <Chip
            label={`{{${entry.position}}}`}
            size="small"
            sx={{ fontFamily: 'monospace', fontWeight: 700, bgcolor: '#fef3c7', color: '#92400e' }}
          />
          <Typography variant="caption" sx={{ color: '#64748b' }}>
            no {COMPONENT_LABELS[entry.component] || entry.component}
          </Typography>
        </Stack>
      )}

      <TextField
        select fullWidth size="small"
        label={compact ? 'De onde vem' : 'De onde vem o valor'}
        value={entry.source_kind}
        onChange={(e) => changeSource(e.target.value)}
        disabled={disabled}
        helperText={cfg?.hint}
      >
        {SOURCES.map((s) => (
          <MenuItem
            key={s.value} value={s.value}
            disabled={s.value === 'CUSTOM_FIELD' && semCampos}
          >
            {s.label}
          </MenuItem>
        ))}
      </TextField>

      {cfg?.needs === 'field' && (
        <TextField
          select fullWidth size="small" label="Qual campo" sx={{ mt: 1.25 }}
          value={entry.source_key || ''}
          onChange={(e) => onChange({ ...entry, source_key: e.target.value })}
          disabled={disabled}
        >
          {customFields.map((f) => (
            <MenuItem key={f.key} value={f.key}>{f.label}</MenuItem>
          ))}
        </TextField>
      )}

      {cfg?.needs === 'text' && (
        <TextField
          fullWidth size="small" label="Texto" sx={{ mt: 1.25 }}
          placeholder="Ex.: Outubro/2026"
          value={entry.fixed_value || ''}
          onChange={(e) => onChange({ ...entry, fixed_value: e.target.value })}
          disabled={disabled}
          inputProps={{ maxLength: 300 }}
        />
      )}
    </Box>
  );
}
